<template>
  <form
    class="form-employee-work-day"
    v-on:reset.prevent="emitCancel"
    v-on:submit.prevent="onSubmit"
  >
    <time
      class="form-employee-work-day__date"
      v-bind:datetime="datetime"
    >{{ dateTitle }}</time>
    <p class="form-employee-work-day__doctor">
      <span class="form-employee-work-day__name"> {{ name }} </span>
      <input-checkbox
        title="Выходной"
        v-bind:disabled="!hasEditSchedule"
        v-model="isOffDay"
      />
    </p>
    <div
      v-for="(cabinet, cabinetIndex) in cabinetsWorkTimes"
      v-bind:key="cabinet.cabinetId || cabinet.key"
      class="form-employee-work-day__group"
    >
      <div class="form-employee-work-day__cabinet-row">
        <the-select
          title="Кресло"
          v-bind:disabled="disableEditWorkTimes"
          v-bind:options="availableCabinets"
          v-bind:value="cabinet.cabinetId"
          v-on:input="onInputCabinetId(cabinetIndex, $event)" />
        <button
          v-if="cabinetIndex > 0"
          class="
            form-employee-work-day__button-icon-remove
            form-employee-work-day__button-cabinet
          "
          title="Удалить"
          type="button"
          v-on:click="onRemoveCabinet(cabinetIndex)"
        />
      </div>
      <span class="form-employee-work-day__label">Часы работы</span>
      <div
        v-for="(workTime, index) in cabinet.workTimes"
        v-bind:key="index"
        class="form-employee-work-day__time-row"
      >
        <time-interval-picker
          class="form-employee-work-day__row"
          v-bind:disabled="disableEditWorkTimes"
          v-bind:initial-from="workTime.from"
          v-bind:initial-to="workTime.to"
          v-bind:time-set="suggestedHours"
          v-on:input-from="onInputWorkTimes(
            cabinetIndex,
            index,
            $event,
            workTime.to,
            workTime.assistantId,
          )"
          v-on:input-to="onInputWorkTimes(
            cabinetIndex,
            index,
            workTime.from,
            $event,
            workTime.assistantId,
          )"
        />
        <v-autocomplete
          clearable
          item-text="full_name"
          item-value="id"
          label="Ассистент"
          no-data-text="Результатов нет"
          v-bind:items="assistants"
          v-model="workTime.assistantId"
        />
        <button
          v-if="index === 0"
          class="form-employee-work-day__button-icon-add"
          title="Добавить"
          type="button"
          v-bind:disabled="cabinet.workTimes.length >= maxWorkIntervalsInDay"
          v-on:click="onAddWorkTimes(cabinetIndex)" />
        <button
          v-else
          class="form-employee-work-day__button-icon-remove"
          title="Удалить"
          type="button"
          v-on:click="onRemoveWorkTimes(cabinetIndex, index)" />
      </div>
    </div>
    <button
      v-if="showAddCabinets"
      class="form-employee-work-day__button-add"
      title="Добавить кресло"
      type="button"
      v-on:click="onAddCabinet">Добавить кресло</button>
    <div class="form-employee-work-day__buttons" v-if="hasEditSchedule">
      <button
        class="form-employee-work-day__cancel"
        type="button"
        v-on:click="emitCancel">Отмена</button>
      <button
        class="form-employee-work-day__submit"
        type="submit">ОК</button>
    </div>
  </form>
</template>
<script>
import InputCheckbox from '@/components/InputCheckbox.vue';
import TheSelect from '@/components/TheSelect.vue';
import TimeIntervalPicker from '@/components/TimeIntervalPicker.vue';

/**
 * @description Название события отмены.
 * @type {string}
 */
const NAME_EVENT_CANCEL = 'cancel';

/**
 * @description Название события отправки формы.
 * @type {string}
 */
const NAME_EVENT_SUBMIT = 'submit';

/**
 * @description Рабочие часы.
 * @param {string} from
 * @param {string} to
 * @param {number} assistantId
 * @constructor
 */
function WorkTimes(from, to, assistantId) {
  this.from = from;
  this.to = to;
  this.assistantId = assistantId;
}

/**
 * @description Кабинет.
 * @param {number}      cabinetId
 * @param {any}         key
 * @param {WorkTimes[]} workTimes
 * @constructor
 */
function Cabinet(cabinetId, key, workTimes) {
  this.cabinetId = cabinetId;
  this.key = key;
  this.workTimes = workTimes;
}

/**
 * @description Компонент формы рабочего дня сотрудника.
 * @type {Vue}
 */
export default {
  components: {
    InputCheckbox,
    TheSelect,
    TimeIntervalPicker,
  },
  computed: {
    /**
     * @description Доступные кабинеты.
     * @returns {any[]}
     */
    availableCabinets() {
      const mapper = (candidate) => ({
        ...candidate,
        disabled: this.selectedCabinets.includes(candidate.value),
      });
      if (this.availableWorkCabinetIds.length === 0) {
        return this.cabinetList.map(mapper);
      }
      const initialCabinetIds = this.initialPeriods.map((period) => period.initialCabinetId);
      return this.cabinetList.flatMap((item) => {
        if (this.availableWorkCabinetIds.includes(item.value)) {
          return [mapper(item)];
        }
        if (initialCabinetIds.includes(item.value)) {
          return [{ ...item, disabled: true }];
        }
        return [];
      });
    },
    selectedCabinets() {
      return this.cabinetsWorkTimes.map(({ cabinetId }) => Number(cabinetId));
    },
    /**
     * @description Показывать кнопку "Добавить кресло".
     * @returns {boolean}
     */
    showAddCabinets() {
      return this.selectedCabinets.length !== this.availableCabinets.length;
    },
    /**
     * @description Деактивировать редактирование часов работы.
     * @returns {boolean}
     */
    disableEditWorkTimes() {
      return this.isOffDay || !this.hasEditSchedule;
    },
  },
  data() {
    return {
      /**
       * @description Часы работы в креслах.
       * @type {Cabinet[]}
       */
      cabinetsWorkTimes: this.initialPeriods.map((period) => new Cabinet(
        period.initialCabinetId,
        null,
        period.initialWorkTimes.map((workTimes) => new WorkTimes(
          workTimes.initialFrom,
          workTimes.initialTo,
          workTimes.initialAssistantId,
        )),
      )),
      /**
       * @description Выходной.
       * @type {boolean}
       */
      isOffDay: false,
    };
  },
  inject: ['maxWorkIntervalsInDay'],
  methods: {
    emitCancel() {
      this.$emit(NAME_EVENT_CANCEL);
    },
    findCabinet(index) {
      return this.cabinetsWorkTimes[index];
    },
    /**
     * @description Обработать добавление кабинета.
     * @returns {void}
     */
    onAddCabinet() {
      this.cabinetsWorkTimes.push({
        cabinetId: null,
        key: Date.now(),
        workTimes: [{ from: '00:00', to: '00:00' }],
      });
    },
    /**
     * @description Обработать добавление часов работы.
     * @param {number} index
     * @returns {void}
     */
    onAddWorkTimes(index) {
      this.findCabinet(index).workTimes.push({ from: '00:00', to: '00:00' });
    },
    /**
     * @description Обработать ввод идентификатора кабинета.
     * @param {number} index
     * @param {number} value
     * @returns {void}
     */
    onInputCabinetId(index, value) {
      this.findCabinet(index).cabinetId = value;
    },
    /**
     * @description Обработать ввод рабочих часов.
     * @param {number} cabinetIndex
     * @param {number} timeIndex
     * @param {string} from
     * @param {string} to
     * @param {number} assistantId
     * @returns {void}
     */
    onInputWorkTimes(cabinetIndex, timeIndex, from, to, assistantId) {
      this.$set(
        this.findCabinet(cabinetIndex).workTimes,
        timeIndex,
        new WorkTimes(from, to, assistantId),
      );
    },
    /**
     * @description Обработать удаление кабинета.
     * @param {number} index
     * @returns {void}
     */
    onRemoveCabinet(index) {
      this.cabinetsWorkTimes.splice(index, 1);
    },
    /**
     * @description Удалить рабочие часы.
     * @param {number} cabinetIndex
     * @param {number} index
     * @returns {void}
     */
    onRemoveWorkTimes(cabinetIndex, index) {
      this.findCabinet(cabinetIndex).workTimes.splice(index, 1);
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      const output = {
        cabinets: this.cabinetsWorkTimes,
        date: this.datetime,
        isOff: this.isOffDay,
      };
      this.$emit(NAME_EVENT_SUBMIT, output);
    },
  },
  name: 'FormEmployeeWorkDay',
  props: {
    /**
     * @description Идентификаторы доступных кабинетов.
     * @type {number[]}
     */
    availableWorkCabinetIds: { default: () => [], type: Array },
    /**
     * @description Список кабинетов.
     * @type {any[]}
     */
    cabinetList: { type: Array, required: true },
    /**
     * @description Заголовок даты.
     * @type {string}
     */
    dateTitle: { default: () => '', type: String },
    /**
     * @description Дата в формате ISO.
     * @type {string}
     */
    datetime: { default: () => '', type: String },
    /**
     * @description Может редактировать график работы.
     * @type {boolean}
     */
    hasEditSchedule: { required: true, type: Boolean },
    initialPeriods: { default: () => [], type: Array },
    /**
     * @description Имя сотрудника.
     * @type {string}
     */
    name: { default: () => '', type: String },
    /**
     * @description Предложенные часы работы.
     * @type {string[]}
     */
    suggestedHours: {
      default: () => [],
      type: Array,
    },
    /**
     * @description Список ассистенов.
     */
    assistants: { default: () => [], type: Array },
  },
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";
$button-font-size: 14px;
$button-height: 37px;
$button-width: 100%;
.form-employee-work-day {
  padding: 15px 20px 27px;
  width: 500px;
  max-width: 99%;
  &__group {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin: 0 0 10px 0;
  }
  &__date {
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    color: #707683;
  }
  &__doctor {
    display: flex;
    flex-direction: row !important;
    align-items: center;
    margin: 5px 0 10px !important;
    label {
      display: flex;
    }
  }
  &__name {
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    color: #192A3E;
  }
  &__cabinet-row,
  &__time-row {
    display: grid;
    grid-template-columns: 1fr 1fr 22px;
    align-items: center;
    grid-gap: 10px;
  }
  &__row {
    display: grid;
    grid-template-columns: 1fr 10px 1fr;
    grid-gap: 10px;
    align-items: center;
  }
  &__input {
    border: 1px solid #C2CFE0;
    box-sizing: border-box;
    border-radius: 4px;
    height: 40px;
    width: 100%;
    padding: 10px;
    color: #707683;
    font-size: 16px;
    &:focus {
      color: #192a3e;
      background-color: #fff;
      border: 1px solid #0489e0 !important;
      outline: 0;
    }
    &:disabled {
      background-color: #fff;
    }
  }
  &__button {
    &-add {
      text-align: left;
      color: #0489e0;
      font-weight: 400;
      font-size: 16px;
      line-height: 20px;
    }
    &-remove {
      text-align: left;
      color: var(--warning-color);
      font-weight: 400;
      font-size: 16px;
      line-height: 20px;
    }
    &-icon-add {
      @include buttons.btn-plus-color-add;
    }
    &-icon-remove {
      @include buttons.btn-cross-color-remove;
    }
    &-cabinet {
      transform: translateY(8px);
    }
  }
  &__buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
    margin: 30px 0 0 0;
  }
  &__cancel {
    @include buttons.btn-transparent($button-width, $button-height, $button-font-size);
  }
  &__submit {
    @include buttons.btn-blue($button-width, $button-height, $button-font-size);
  }
  &__label {
    color: #707683;
    font-size: 12px;
  }
}

@media screen and (max-width: 480px) {
 .form-employee-work-day {
  &__cabinet-row {
    grid-template-columns: 1fr 22px;
  }
  &__time-row {
    grid-template-columns: 1fr 22px;
  }
  &__row {
    grid-column: 1 / -1;
  }
 }
}
</style>
